import { useEffect } from 'react'
import { useRouter } from 'next/router'

import { summarizeObjects } from 'utils/object'
import { useApiGetAllPerencanaanDinas } from 'api/kinerja/kepala-sekolah/dinas/perencanaan'
import { useApiGetAllPerencanaanDinasTabGuru } from 'api/kinerja/kepala-sekolah/guru/dinas/perencanaan'

import type {
  PerencanaanKsByDinasRole,
  PerencanaanKsByDinasState,
  PerencanaanKsByDinasTipe,
  TabFilterRoles,
} from 'types/kinerja-ks'

type Props = {
  activeTabName: TabFilterRoles
  state?: PerencanaanKsByDinasState
  role?: PerencanaanKsByDinasRole
  tipe?: PerencanaanKsByDinasTipe
  onBothFetched?: () => void
}

const usePerencanaanDinas = ({
  activeTabName,
  state = 'ALL',
  role,
  tipe,
  onBothFetched,
}: Props) => {
  const router = useRouter()

  const isKs = ['KS', 'KS_PLT'].includes(activeTabName)

  // Calculations for Kepala Sekolah
  const {
    isLoading: isListLoadingKs,
    isError: isListErrorKs,
    isSuccess: isListSuccessKs,
    isFetched: isListFetchedKs,
    data: { data: itemsKs, meta: metasKs } = {},
    refetch: refetchListKs,
  } = useApiGetAllPerencanaanDinas(
    {
      state,
      role,
      tipe,
    },
    { enabled: isKs && router.isReady }
  )

  // Calculations for Guru
  const {
    isLoading: isListLoadingGuru,
    isError: isListErrorGuru,
    isSuccess: isListSuccessGuru,
    isFetched: isListFetchedGuru,
    data: { data: itemsGuru, meta: metasGuru } = {},
    refetch: refetchListGuru,
  } = useApiGetAllPerencanaanDinasTabGuru(
    {
      state,
      role,
      tipe,
    },
    { enabled: ['GURU'].includes(activeTabName) && router.isReady }
  )

  const isListLoading = isListLoadingKs || isListLoadingGuru
  const isListError = isListErrorKs || isListErrorGuru
  const isListSuccess = isListSuccessKs || isListSuccessGuru
  const isListBothFetched = isListFetchedKs && isListFetchedGuru

  // Items based on active tab
  const items = isKs ? itemsKs : itemsGuru
  const itemsKsCount = itemsKs?.length || 0
  const itemsGuruCount = itemsGuru?.length || 0

  // Total items
  const totalItems = itemsKsCount + itemsGuruCount

  const metas = {
    ...metasKs,
    counts: summarizeObjects(metasKs?.counts, metasGuru?.counts),
  }

  useEffect(() => {
    if (isListBothFetched && !isListLoading) onBothFetched?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListBothFetched])

  return {
    // Kepala Sekolah
    isListLoadingKs,
    isListErrorKs,
    isListSuccessKs,
    itemsKs,
    itemsCountKs: itemsKsCount,
    metasKs,
    refetchListKs,

    // Guru
    isListLoadingGuru,
    isListErrorGuru,
    isListSuccessGuru,
    itemsGuru,
    itemsCountGuru: itemsGuruCount,
    metasGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    isListError,
    isListSuccess,
    isListBothFetched,
    metas,
    totalItems,
    hasItems: totalItems > 0,

    // Relative to active tab
    items,
    relativeItems: items, // Serve as an alias for items
    isRelativeLoading: isKs ? isListLoadingKs : isListLoadingGuru,
    isRelativeError: isKs ? isListErrorKs : isListErrorGuru,
    isRelativeSuccess: isKs ? isListSuccessKs : isListSuccessGuru,
  }
}

export default usePerencanaanDinas
